import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

// Components
import Textarea from '@atoms/Textarea';
import Alert from '@atoms/Alert';
import SubmitButton from '@atoms/SubmitButton';
import MemberListItemRedesign from '../MemberListItemRedesign'; // eslint-disable-line
import { ButtonPrimaryLarge } from '@styled';

// Functionality
import { GlobalModalContext } from '@context/globalModalContext';
import useSendMessageOutsideInbox from '@hooks/useSendMessageOutsideInbox';

const getInitialValues = () => ({});

export const useSendMessageModal = () => {
    const [, setIsModalOpen, setModalChildren] = useContext(GlobalModalContext);

    const openModal = ({
        businessType,
        companyID,
        companyName,
        designations,
        displayLocation,
        firstName,
        fullName,
        graphID,
        id,
        lastName,
        photoURL,
        title,
        url,
        isFavorite,
        messageAttributes,
        onMessage,
        showMemberDetails,
        showMemberDetailsCTA,
    }) => {
        setModalChildren(() => (
            <SendMessageModal
                businessType={businessType}
                companyID={companyID}
                companyName={companyName}
                designations={designations}
                displayLocation={displayLocation}
                firstName={firstName}
                fullName={fullName}
                graphID={graphID}
                id={id}
                lastName={lastName}
                photoURL={photoURL}
                title={title}
                url={url}
                isFavorite={isFavorite}
                messageAttributes={messageAttributes}
                onMessage={onMessage}
                showMemberDetails={showMemberDetails}
                showMemberDetailsCTA={showMemberDetailsCTA}
            />
        ));

        setIsModalOpen(true);
    };

    return { openModal };
};

const SendMessageModal = props => {
    const {
        id,
        graphID,
        showMemberDetails = true,
        showMemberDetailsCTA = true,
        companyID,
        companyName,
        displayLocation,
        fullName,
        firstName,
        lastName,
        designations,
        businessType,
        title,
        isFavorite,
        photoURL,
        messageAttributes,
        onMessage = () => {},
    } = props;

    const [messageText, setMessageText] = useState('');
    const [, setIsModalOpen] = useContext(GlobalModalContext);
    const { sendMessage, isLoading, error, isSuccess } = useSendMessageOutsideInbox();

    useEffect(() => {
        if (isSuccess) {
            onMessage(props);
        }
    }, [isSuccess, onMessage, props]);

    const handleSubmit = async () => {
        if (messageText === '') return;
        sendMessage({ recipientID: graphID, messageText, messageAttributes: JSON.stringify(messageAttributes) });
    };

    return (
        <>
            {isSuccess && (
                <div>
                    <h2 className="font-weight-bold">Message Successfully Sent</h2>

                    <p className="font-size-sm">Keep an eye on your inbox for a message back from {fullName}</p>

                    <div className="mt-2">
                        <ButtonPrimaryLarge onClick={() => setIsModalOpen(false)}>Ok</ButtonPrimaryLarge>
                    </div>
                </div>
            )}
            {!isSuccess && (
                <Formik onSubmit={() => handleSubmit()} enableReinitialize initialValues={getInitialValues()}>
                    <Form noValidate>
                        <div
                            className="accent font-weight-bold font-redhat font-size-smaller"
                            style={{ marginBottom: '.3rem' }}
                        >
                            Send a message
                        </div>
                        <h2 className="font-weight-bold" style={{ marginBottom: '2rem' }}>
                            Talk with <span>{fullName}</span>
                        </h2>

                        {id && showMemberDetails && (
                            <div>
                                <MemberListItemRedesign
                                    id={id}
                                    graphID={graphID}
                                    companyID={companyID}
                                    companyName={companyName}
                                    title={title}
                                    fullName={fullName}
                                    firstName={firstName}
                                    lastName={lastName}
                                    designations={designations}
                                    displayLocation={displayLocation}
                                    businessType={businessType}
                                    isFavorite={isFavorite}
                                    showMailIcon={false}
                                    photoURL={photoURL}
                                    url={`/member/profile/${id}`}
                                    customStyle={{ borderTopColor: '#d8d8d8' }}
                                    showCTA={showMemberDetailsCTA}
                                />
                            </div>
                        )}

                        <div className="pt-2" style={{ borderTop: '1px solid #D8D8D8' }}>
                            {error && (
                                <Alert
                                    status="danger"
                                    msg="There was an error sending your message."
                                    style={{ marginBottom: 0, marginTop: '14px' }}
                                />
                            )}
                            <Textarea
                                name="message"
                                placeholder="Write a message..."
                                rows="10"
                                style={{ border: '1px solid #d8d8d8' }}
                                value={messageText}
                                onChange={e => setMessageText(e.target.value)}
                            />
                        </div>

                        <div className="mt-4">
                            <SubmitButton
                                buttonClasses="sendmessage"
                                successText="Sent!"
                                submittingText="Sending..."
                                isDisabled={isLoading && !error}
                                isSubmitting={isLoading && !error}
                            >
                                Submit
                            </SubmitButton>
                        </div>
                    </Form>
                </Formik>
            )}
        </>
    );
};

SendMessageModal.propTypes = {
    id: PropTypes.number,
    graphID: PropTypes.string.isRequired,
    showMemberDetails: PropTypes.bool,
    showMemberDetailsCTA: PropTypes.bool,
    companyID: PropTypes.number,
    companyName: PropTypes.string,
    title: PropTypes.string,
    fullName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    designations: PropTypes.string,
    displayLocation: PropTypes.string,
    businessType: PropTypes.array,
    isFavorite: PropTypes.bool,
    photoURL: PropTypes.string,
    messageAttributes: PropTypes.object,
    onMessage: PropTypes.func,
};

export default SendMessageModal;
